import { useContext, useEffect, useState } from 'react';

import SelectSurveyAnswerApi from '../../../api/selectSurveyAnswerApi';
import { SelectSurveyContentData, SelectType } from '../../../types/contentTypes';
import { AuthContext } from '../../../utils/AuthProvider';
import { textToParagraphs, xorArray } from '../../../utils/functions';
import OptionListItem from '../../atoms/option-list-item/OptionListItem';

interface SelectSurveyContentProps {
  content: SelectSurveyContentData;
  onComplete: (() => void) | undefined;
}

export default function SelectSurveyContent({ content, onComplete }: SelectSurveyContentProps) {
  const [selectedAlternatives, setSelectedAlternatives] = useState<string[]>([]);

  const optionsInputType = (() => {
    switch (content.selectType) {
      case SelectType.Single:
        return 'radio';
      case SelectType.Multiple:
        return 'checkbox';
    }
  })();

  const { user } = useContext(AuthContext)!;

  useEffect(() => {
    async function fetchData() {
      if (!user) return;
      const fetchedSurveyAnswer = await SelectSurveyAnswerApi.getUserAnswerForContent(content.id, user.uid);
      if (fetchedSurveyAnswer) {
        setSelectedAlternatives(fetchedSurveyAnswer.selectedAlternatives);
      }
    }

    fetchData();
  }, [content.id, user]);

  // Methods
  async function handleAlternativeClick(alternative: string) {
    let newSelectedAlternatives: string[];
    switch (content.selectType) {
      case SelectType.Single:
        newSelectedAlternatives = alternative === selectedAlternatives[0] ? [] : [alternative];
        break;
      case SelectType.Multiple:
        newSelectedAlternatives = xorArray(selectedAlternatives, alternative);
        break;
    }
    setSelectedAlternatives(newSelectedAlternatives);
    await SelectSurveyAnswerApi.addOrUpdateSurveyAnswer({
      surveyContentId: content.id,
      userId: user!.uid,
      selectedAlternatives: newSelectedAlternatives,
    });
    onComplete?.();
  }

  const OptionsList = content.alternatives?.map(alternative => {
    return (
      <OptionListItem
        key={alternative.key}
        text={alternative.text}
        onClick={() => handleAlternativeClick(alternative.key)}
        checked={selectedAlternatives.includes(alternative.key)}
        buttonType={optionsInputType}
      />
    );
  });

  return (
    <section className="select-survey-content">
      <h2 className="strong-heading mb-8">{content.headline}</h2>
      <div className="mb-12">{textToParagraphs(content.description)}</div>
      <ul className="select-survey-list mb-5">{OptionsList}</ul>
    </section>
  );
}
